import React, {useCallback, useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import {useLocation} from 'react-router-dom';
import styled from 'styled-components';

import {dispatch, EVENT_ID, subscribe, unsubscribe} from '../../../../containers/event_manager';
import {CSS_CONSTANTS} from '../../../../css_variables';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import useEventListener from '../../hooks/useEventListener';
import SlideoutContentWrapper from './SlideoutContentWrapper';

interface SlideoutData {
	slideoutId: string;
	width?: number;
	content: React.ReactNode;
	trackingName: string;
	cy?: string;
	noHeader?: boolean;
	invokerRef: React.MutableRefObject<HTMLElement | null>;
}

export const toggleSlideout = (slideoutId: string, slideoutData: Omit<SlideoutData, 'slideoutId'>) =>
	dispatch(EVENT_ID.TOGGLE_SLIDEOUT, slideoutId, slideoutData);

export const closeSlideout = (refocus?: boolean) => dispatch(EVENT_ID.CLOSE_SLIDEOUT, refocus);

interface SlideoutContainerProps {
	shouldShow?: boolean;
	noHeader?: boolean;
	width: number;
}

const SlideoutContainer = styled.div<SlideoutContainerProps>`
	z-index: 99;
	box-sizing: border-box;
	position: fixed;
	width: ${props => (props.shouldShow ? `${props.width}px` : '0px')};
	display: flex;
	flex-direction: column;
	top: 0;
	bottom: 0;
	right: 0;
	margin-top: ${props => (props.noHeader ? '36px' : '131px')};
	padding: ${props => (props.shouldShow ? '34px 30px' : 0)};
	border-left: ${props => (props.shouldShow ? `1px solid ${CSS_CONSTANTS.v2_border_gray}` : 'none')};
	border-top: 1px solid ${CSS_CONSTANTS.v2_border_gray};
	background-color: white;
	box-shadow: 0px 3px 5px 0px rgba(74, 89, 105, 0.14);
	transition: width 0.1s ease-in-out;
`;

const initialState = {} as const;

const SlideoutConductor = (): React.ReactNode => {
	const [slideoutData, setSlideoutData] = useState<SlideoutData | Record<string, never>>(initialState);
	const location = useLocation();

	const openSlideout = (id: string, data: Omit<SlideoutData, 'slideoutId'>) => {
		if (data.trackingName) {
			trackEvent(data.trackingName, 'Toggled', {expanded: true});
		}
		setSlideoutData({slideoutId: id, ...data});
	};

	const closeSlideoutInternal = useCallback(
		(refocus?: boolean) => {
			if (slideoutData.content) {
				if (slideoutData.trackingName) {
					trackEvent(slideoutData.trackingName, 'Toggled', {expanded: false});
				}
				setSlideoutData(initialState);

				if (refocus && slideoutData.invokerRef?.current) {
					slideoutData.invokerRef.current?.focus();
				}
			}
		},
		[slideoutData]
	);

	const toggleSlideout = useCallback(
		(id, data) => {
			if (id !== slideoutData?.slideoutId) {
				openSlideout(id, data);
			} else {
				closeSlideoutInternal();
			}
		},
		[slideoutData]
	);

	const handleKeyDown = useCallback(
		keyboardEvent => {
			if (keyboardEvent.key === 'Escape') {
				closeSlideout(true);
			}
		},
		[slideoutData]
	);

	useEffect(() => {
		subscribe(EVENT_ID.TOGGLE_SLIDEOUT, toggleSlideout);
		subscribe(EVENT_ID.CLOSE_SLIDEOUT, closeSlideoutInternal);
		return () => {
			unsubscribe(EVENT_ID.TOGGLE_SLIDEOUT, toggleSlideout);
			unsubscribe(EVENT_ID.CLOSE_SLIDEOUT, closeSlideoutInternal);
		};
	}, [slideoutData]);

	useEffect(() => {
		closeSlideout();
	}, [location]);
	useEventListener('keydown', handleKeyDown);

	const portalRoot = document.querySelector('#root-portal-container');

	return portalRoot
		? ReactDOM.createPortal(
				<SlideoutContainer
					shouldShow={!!slideoutData.slideoutId}
					width={slideoutData.width || 535}
					noHeader={slideoutData.noHeader}
					data-cy={slideoutData.cy}
					onClick={e => e.stopPropagation()}
				>
					{slideoutData.content ? <SlideoutContentWrapper>{slideoutData.content}</SlideoutContentWrapper> : null}
				</SlideoutContainer>,
				portalRoot
		  )
		: null;
};

export default SlideoutConductor;
